import { instanceToInstance } from 'class-transformer';


// The Base class has fields that are common for all of our objects.
//
// uuid: This is a string created on the client using the uuidv4() function. Because the app is offline-first,
//       the uuid must be created on the client and can never be changed by the server.
//
// synced: This is the time in seconds since 1970 when the server updated the object. It
//       is created and updated on the server. The main purpose of synced is to determine
//       if we should use a POST or a PUT. We POST when synced is null, and PUT otherwise.
//
// sendDate: This is the time in seconds since 1970 when the client sent the object to the server.
//       It is created and updated on the client and is NOT sent to the server. We use the sendDate
//       as part of the isDirty calculation.
//
// dirtyDate: This is the time in seconds since 1970 when the data was last updated on the client.
//       It is created and updated on the client and stored on the server. We use it on the client
//       to figure out if an object is dirty by comparing it to the sendDate. If the dirtyDate
//       is after the sendDate, then the object is dirty. Because sending is an asynchronous
//       process, it is possible for us to update the dirtyDate after we have sent the object.
//       We also use dirtyDate on the server to know if the item sent up is dirty. If the number
//       is higher than the item on the server, then that item is replaced. This is important
//       since it prevents someone for editing things offline and then turning on their device
//       months later and have it overwrite newer info on the server.
//
// isDeleted: A flag that indicates the object has been deleted. It is updated on the client and
//       is not stored on the server.


export class Base {
    uuid: string
    synced: number        // TODO: change to syncDate
    dirtyDate: number
    sendDate: number
    isDeleted: boolean = false

    private static lastNow = 0

    private now(): number {
        // We need to ensure that now() will never return exactly the same number of seconds
        // because we use it to set both the dirtyDate and the sendDate. Something is
        // dirty if it has changed after its send date and it is possible we could change
        // an item the same second that we send it. (Not just possible -- happens often)
        let now = Math.round((new Date()).getTime() / 1000)  // https://stackoverflow.com/questions/9456138/how-can-i-get-seconds-since-epoch-in-javascript
        now *= 1000000  // time in microseconds; this allows up to 1M writes in a single second
        if (now <= Base.lastNow) {
            now = Base.lastNow + 1
        }
        Base.lastNow = now
        return now
    }

    // an object is dirty if it has been changed after it has been sent to server
    get isDirty(): boolean { 
        const isDirty = (this.dirtyDate || 0) > (this.sendDate || 0)
        return isDirty
    }  
    set isDirty(dirty: boolean) { 
        this.dirtyDate = dirty ? this.now() : 0
    }

    setSendDate() { this.sendDate = this.now()}
    resetSendDate() { this.sendDate = this.dirtyDate }  // mark it not dirty

    constructor() {
        this.uuid = null
        this.synced = null
        this.dirtyDate = null
        this.sendDate = null
        this.isDeleted = false
    }

    unsavedCopy(): Base {
        const base = instanceToInstance(this);
        base.uuid = null
        base.synced = null
        base.dirtyDate = null
        base.sendDate = null
        base.isDeleted = false
        return base
    }

    copyFrom(base: Base) {
        // Don't copy uuid, but do copy all of the other fields. Goal is to replace all data from the other Base object so can be synced to server.
        this.synced = base.synced
        this.dirtyDate = base.dirtyDate
        this.sendDate = base.sendDate
        this.isDeleted = base.isDeleted
    }
}
